/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { useSaveUserProfile, useUserProfile } from '@/api/hooks';
import type { UserProfileUpdate } from '@/api/settings';
import { isValidEmail, isValidPhone, resolve } from '@/lib/profileField';
import { getRoles } from '@/lib/tokenClaims';
import { useIsDealerUser } from '@/lib/userRole';
import { useSeededForm } from '@/lib/useSeededForm';
import { Icon } from '@/components/Icon';
import { InlineLoading } from '@/components/loading/LoadingDots';
import { toast } from '@/stores/toastStore';
import { DealershipAddressFields } from './DealershipAddressFields';
import { EditableField, ReadonlyField, TextField } from './ProfileFields';
import { useDealershipAddress } from './useDealershipAddress';
import { Modal } from './Modal';

/** Profile dialog: read-only for internal users; editable contact and address for dealers. */
export function ProfileModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { account, name, initials } = useAuth();
  const isDealer = useIsDealerUser();
  const gmin = account?.gmin ?? '';

  // One request, already resolved against Entra and GDL. The dialog used to fetch the saved row and
  // the dealer locator separately and merge them itself, which meant the rule deciding what is
  // read-only lived here as well as in the server that enforces it on save.
  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
  } = useUserProfile(gmin, isDealer);
  const saveProfile = useSaveUserProfile();

  // Raised by a save attempt, so every complaint appears at once rather than one field at a time.
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // The dealership address, resolved and validated the same way the feedback dialog resolves it.
  const address = useDealershipAddress(profile, isDealer);

  // The identity half, seeded from the profile. Fields a system of record claimed are seeded too
  // but never read back: `resolve` returns the server's value for those.
  const seed = useMemo(
    () => ({
      firstName: profile?.firstName ?? '',
      lastName: profile?.lastName ?? '',
      email: profile?.email ?? '',
      secondaryEmail: profile?.secondaryEmail ?? '',
      phone: profile?.phone?.trim() ?? '',
      alternatePhone: profile?.alternatePhone ?? '',
    }),
    [profile],
  );
  const { values: entered, change } = useSeededForm(seed);

  // UPN local part is the GM login id.
  const loginId = account?.username?.split('@')[0]?.toLowerCase() ?? '';
  // Internal users have no profile row to read, so their read-only identity comes off the token.
  const email = account?.username ?? '';
  const [first, last] = useMemo<[string, string]>(() => {
    const [f = '', ...rest] = (name ?? '').trim().split(/\s+/);
    return [f, rest.join(' ')];
  }, [name]);

  const accountRows: [string, string][] = [
    account?.gmin ? ['GMIN', account.gmin] : null,
    loginId ? [t('profile.labels.userId'), loginId] : null,
  ].filter(Boolean) as [string, string][];
  const roles = getRoles();

  // The identity half as the server resolved it: Entra for who the user is, GDL for the number it
  // holds. Whatever those answered is shown read-only; the rest is theirs to fill in.
  const fields = {
    firstName: resolve(profile, 'firstName', entered.firstName),
    lastName: resolve(profile, 'lastName', entered.lastName),
    email: resolve(profile, 'email', entered.email),
    phone: resolve(profile, 'phone', entered.phone),
  };

  // A locked field is never missing: the system of record has already answered for it.
  const missingIdentity = isDealer
    ? [fields.firstName, fields.lastName, fields.email, fields.phone].filter((f) => !f.value.trim())
        .length
    : 0;
  const addressIncomplete = isDealer && !address.complete;
  const missingCount = missingIdentity + (isDealer ? address.missing.length : 0);

  const phoneError =
    !fields.phone.locked && !isValidPhone(entered.phone) ? t('profile.phoneInvalid') : null;
  const emailError =
    !fields.email.locked && !isValidEmail(fields.email.value) ? t('profile.emailInvalid') : null;
  const secondaryEmailError = !isValidEmail(entered.secondaryEmail)
    ? t('profile.emailInvalid')
    : null;
  const alternatePhoneError = !isValidPhone(entered.alternatePhone)
    ? t('profile.phoneInvalid')
    : null;
  const malformed = Boolean(phoneError || emailError || secondaryEmailError || alternatePhoneError);
  const incomplete = missingCount > 0 || malformed;

  // Locked fields are sent as resolved for a consistent stored row; the server re-derives them
  // anyway, so a tampered payload cannot overwrite what Entra or GDL says.
  const payload = {
    firstName: fields.firstName.value.trim(),
    lastName: fields.lastName.value.trim(),
    email: fields.email.value.trim(),
    secondaryEmail: entered.secondaryEmail.trim(),
    phone: fields.phone.value.trim(),
    alternatePhone: entered.alternatePhone.trim(),
    ...address.values,
  } satisfies UserProfileUpdate;

  // Save goes dead only when pressing it would achieve nothing: nothing changed and nothing wrong.
  // Where Entra and GDL answer for every field that is the normal state of this dialog, and a live
  // button there reads as a form the user has failed to finish rather than one already complete.
  // An incomplete form keeps the button, since clicking it is how the complaints are revealed.
  const dirty = (Object.keys(payload) as (keyof typeof payload)[]).some(
    (key) => payload[key] !== (profile?.[key] ?? '').trim(),
  );
  const nothingToDo = !dirty && !incomplete;

  const save = async () => {
    // Nothing is persisted while a required detail is absent or a value is malformed; revealing
    // every complaint at once beats rejecting the form one field at a time.
    setSubmitted(true);
    if (incomplete) {
      return;
    }
    if (!gmin) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError(false);
    const ok = await saveProfile(gmin, payload);
    setSaving(false);
    // Keep the modal open on failure so the user sees the error instead of a silent no-op.
    if (ok) {
      toast.success(t('toast.profileSaved'));
      onClose();
    } else {
      setSaveError(true);
      toast.error(t('profile.saveError'));
    }
  };

  // Dealers supply identity only where the token is silent; internal users never edit it.
  const displayFirst = isDealer ? fields.firstName.value : first;
  const displayLast = isDealer ? fields.lastName.value : last;
  const displayEmail = isDealer ? fields.email.value : email;
  const displayName = [displayFirst, displayLast].filter(Boolean).join(' ') || name || '—';

  return (
    <Modal onClose={onClose} labelledBy="profile-title">
      <div className="modal prof-modal">
        <div className="modal-head">
          <Icon
            name="user"
            size={22}
            style={{ color: 'var(--color-primary-text)', flex: 'none' }}
          />
          <h2 id="profile-title">{t('profile.title')}</h2>
          <div className="sp" style={{ flex: 1 }} />
          <button className="iclose" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="ov-body prof-body">
          <div className="pf-cols">
            <div className="pf-left">
              <div className="pf-hero2">
                <span className="prof-av">{initials || 'JT'}</span>
                <div className="prof-name">{displayName}</div>
                <div className="prof-mail">{displayEmail || '—'}</div>
              </div>
              <div className="pf-sec">{t('profile.account')}</div>
              <div className="pf-readlist">
                {accountRows.map(([k, v]) => (
                  <div className="pf-readrow" key={k}>
                    <span className="pf-rk">{k}</span>
                    <span className="pf-rv">{v}</span>
                  </div>
                ))}
                {/* Roles drive what internal staff can reach. A dealer has the one role, so the
                    row would say the same thing for every dealer who ever opened this. */}
                {!isDealer && (
                  <div className="pf-readrow">
                    <span className="pf-rk">{t('profile.labels.roles')}</span>
                    {roles.length > 0 ? (
                      <ul className="pf-roles" aria-label={t('profile.labels.roles')}>
                        {[...new Set(roles)].map((role) => (
                          <li key={role} className="pf-role-chip">
                            {role}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="pf-rv">{t('profile.noRoles')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="pf-right">
              {missingCount > 0 && (
                <div className="pf-alert">
                  <Icon name="info" size={16} />
                  <span>
                    {t('profile.missingAlert', {
                      count: missingCount,
                      suffix: missingCount === 1 ? '' : 's',
                      dealerPart: addressIncomplete ? t('profile.missingDealerPart') : '',
                    })}
                  </span>
                </div>
              )}
              <div className="pf-sec">{t('profile.contactDetails')}</div>
              <div className="pf-grid">
                {isDealer ? (
                  <TextField
                    label={t('profile.firstName')}
                    field={fields.firstName}
                    onChange={change('firstName')}
                    required
                  />
                ) : (
                  <ReadonlyField label={t('profile.firstName')} value={first} />
                )}
                {isDealer ? (
                  <TextField
                    label={t('profile.lastName')}
                    field={fields.lastName}
                    onChange={change('lastName')}
                    required
                  />
                ) : (
                  <ReadonlyField label={t('profile.lastName')} value={last} />
                )}
                {isDealer && (
                  <TextField
                    label={t('profile.phoneNumber')}
                    field={fields.phone}
                    onChange={change('phone')}
                    required
                    inputMode="tel"
                    placeholder={t('profile.phonePlaceholder')}
                    invalid={phoneError}
                    revealErrors={submitted}
                  />
                )}
                {/* No placeholder: it would repeat the one above it, and the label already says
                    what this is. */}
                {isDealer && (
                  <EditableField
                    label={t('profile.alternatePhone')}
                    value={entered.alternatePhone}
                    onChange={change('alternatePhone')}
                    optional
                    inputMode="tel"
                    invalid={alternatePhoneError}
                    revealErrors={submitted}
                  />
                )}
                {isDealer ? (
                  <TextField
                    label={t('profile.email')}
                    field={fields.email}
                    onChange={change('email')}
                    required
                    inputMode="email"
                    invalid={emailError}
                    revealErrors={submitted}
                  />
                ) : (
                  <ReadonlyField label={t('profile.email')} value={email} />
                )}
                {isDealer && (
                  <EditableField
                    label={t('profile.secondaryEmail')}
                    value={entered.secondaryEmail}
                    onChange={change('secondaryEmail')}
                    optional
                    inputMode="email"
                    placeholder={t('profile.secondaryEmailPlaceholder')}
                    invalid={secondaryEmailError}
                    revealErrors={submitted}
                  />
                )}
              </div>

              {isDealer && (
                <>
                  <div className="pf-sec">{t('profile.dealershipAddress')}</div>
                  {profileLoading ? (
                    <div className="pf-empty">
                      <InlineLoading label={t('profile.dealerGdlLoading')} />
                    </div>
                  ) : (
                    <>
                      {(profileError || (address.gdlSuppliedNothing && !address.anyValue)) && (
                        <p className="pf-empty">
                          {t(profileError ? 'profile.dealerGdlError' : 'profile.dealerGdlEmpty')}
                        </p>
                      )}
                      <DealershipAddressFields address={address} />
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="ov-foot">
          {submitted && incomplete && (
            <span className="pf-err" role="alert">
              {t('profile.incomplete')}
            </span>
          )}
          {saveError && (
            <span className="pf-err" role="alert">
              {t('profile.saveError')}
            </span>
          )}
          {isDealer && nothingToDo && !profileLoading && (
            <span className="pf-settled">{t('profile.upToDate')}</span>
          )}
          {/* Alone (nothing to save) it is the dialog's one action, so primary like every other
              lone dismiss button; beside Save it is the secondary way out. */}
          <button
            className={isDealer ? 'btn btn-ghost' : 'btn btn-pri'}
            onClick={onClose}
            disabled={saving}
          >
            {t('common.close')}
          </button>
          {isDealer && (
            <button
              className="btn btn-pri"
              onClick={save}
              disabled={saving || profileLoading || nothingToDo}
            >
              <Icon name="check" size={16} /> {t('profile.saveProfile')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
